import { useState, useCallback } from 'react'
import { Location } from '../SearchLocation.types'
import { MapAdapter } from '../adapters/types'
import { validateLocation, LocationValidator } from '../core/locationValidator'

/**
 * Location resolution function type
 * Transforms a location before selection (e.g., address to building)
 */
export type LocationResolver = (location: Location) => Promise<Location | null>

/**
 * Options for location selection hook
 */
export interface UseLocationSelectionOptions {
  /** Callback when location is selected or cleared */
  onChange?: (location: Location | null) => void
  /** Callback when location is selected (backward compatibility) */
  onLocationSelect?: (location: Location | null) => void
  /** Optional validation function */
  validateSelection?: LocationValidator
  /** Optional resolution function to transform location */
  resolveLocation?: LocationResolver
  /** Map adapter for navigation (optional) */
  mapAdapter?: MapAdapter
}

/**
 * Return type for location selection hook
 */
export interface UseLocationSelectionReturn {
  /** Currently selected location */
  selectedLocation: Location | null
  /** Validation error message (if any) */
  validationError: string | null
  /** Whether location is being resolved */
  isResolving: boolean
  /** Handle location selection */
  handleSelect: (location: Location) => Promise<void>
  /** Handle clearing selection */
  handleClear: () => void
  /** Clear validation error */
  clearValidationError: () => void
}

/**
 * Hook that manages location selection with validation, resolution, and map navigation
 *
 * @param options - Configuration options
 * @returns Selection state and handlers
 */
export const useLocationSelection = ({
  onChange,
  onLocationSelect,
  validateSelection,
  resolveLocation,
  mapAdapter,
}: UseLocationSelectionOptions = {}): UseLocationSelectionReturn => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  )
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isResolving, setIsResolving] = useState(false)

  const clearValidationError = useCallback(() => {
    setValidationError(null)
  }, [])

  const handleSelect = useCallback(
    async (location: Location) => {
      // Clear any previous validation error
      setValidationError(null)

      // Step 1: Validate the location
      if (validateSelection) {
        const { isValid, error } = validateLocation(location, validateSelection)
        if (!isValid) {
          setValidationError(error)
          return
        }
      }

      // Step 2: Resolve the location if needed
      let finalLocation = location
      if (resolveLocation) {
        setIsResolving(true)
        try {
          const resolved = await resolveLocation(location)
          if (!resolved) {
            setValidationError('Unable to resolve location')
            setIsResolving(false)
            return
          }
          finalLocation = resolved
        } catch (error) {
          console.error('Error resolving location:', error)
          setValidationError('Error resolving location')
          setIsResolving(false)
          return
        }
        setIsResolving(false)
      }

      // Step 3: Update state
      setSelectedLocation(finalLocation)

      // Step 4: Navigate map if adapter is available
      if (mapAdapter) {
        mapAdapter.navigateToLocation(finalLocation)
      }

      // Step 5: Call callbacks
      onChange?.(finalLocation)
      onLocationSelect?.(finalLocation)
    },
    [validateSelection, resolveLocation, mapAdapter, onChange, onLocationSelect]
  )

  const handleClear = useCallback(() => {
    setSelectedLocation(null)
    setValidationError(null)
    onChange?.(null)
    onLocationSelect?.(null)
  }, [onChange, onLocationSelect])

  return {
    selectedLocation,
    validationError,
    isResolving,
    handleSelect,
    handleClear,
    clearValidationError,
  }
}
